import React, { useEffect, useState } from 'react';
import { keyLabel } from '../../../services/shortcuts/keys';
import { bindingsOf, Shortcut, ShortcutGroup } from '../../../services/shortcuts/registry';
import useShortcut, { useShortcutList } from '../../../services/shortcuts/useShortcut';
import Dialog from '../dialog/dialog';
import s from './shortcutsOverlay.module.scss';

/**
 * The list of keyboard shortcuts, on `?`.
 *
 * Read out of the shortcut registry, so it shows what is registered right
 * now - the library shortcuts while a playlist is up, a dialog's Escape
 * while one is open, which includes this one - and can never drift from
 * what the keys actually do. Mounted once, at the app's root, and it also
 * shows the chord prefix that is waiting for its second key ("g …") as a
 * small badge, so a chord half typed is not a mystery.
 */

/** Dispatched on document by anything that wants the list open - the button on the settings page. */
export const showShortcutsEvent = 'fadila:show-shortcuts';

export function showShortcuts() {
  document.dispatchEvent(new CustomEvent(showShortcutsEvent));
}

const groupOrder: ShortcutGroup[] = ['Playback', 'Library', 'Navigation', 'Dialogs', 'Help'];

function labelsOf(shortcut: Shortcut) {
  return bindingsOf(shortcut).map((binding, i) => (
    <React.Fragment key={i}>
      {i > 0 && ' or '}
      <kbd className={s.kbd}>{keyLabel(binding)}</kbd>
    </React.Fragment>
  ));
}

export default function ShortcutsOverlay() {
  const [open, setOpen] = useState(false);
  const { shortcuts, pendingPrefix } = useShortcutList();

  useShortcut({
    id: 'shortcuts-overlay',
    keys: { key: '?' },
    group: 'Help',
    description: 'Show this list',
    run: () => {
      setOpen((x) => !x);
    },
  });

  useEffect(function openOnRequest() {
    const show = () => setOpen(true);

    document.addEventListener(showShortcutsEvent, show);

    return () => document.removeEventListener(showShortcutsEvent, show);
  }, []);

  return (
    <>
      {pendingPrefix && (
        <div className={s.pending} aria-live="polite">
          <kbd className={s.kbd}>{pendingPrefix}</kbd> …
        </div>
      )}
      {open && (
        <Dialog title="Keyboard shortcuts" onClose={() => setOpen(false)} size="large">
          {groupOrder.map((group) => {
            const rows = shortcuts.filter((x) => x.group === group);

            if (rows.length === 0) {
              return null;
            }

            return (
              <section key={group} className={s.group}>
                <h6 className={s.groupTitle}>{group}</h6>
                <table className={s.table}>
                  <tbody>
                    {rows.map((x) => (
                      <tr key={x.id}>
                        <td className={s.keys}>{labelsOf(x)}</td>
                        <td>{x.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            );
          })}
          <p className={s.note}>
            Keys without a modifier are left alone while you are typing in a box.
          </p>
        </Dialog>
      )}
    </>
  );
}
